import { Navigate, Route, Routes } from "react-router-dom";

import ReviewQueuePage from "@/ADMIN/feature/ReviewQueue/ReviewQueuePage";
import ClientFetchQuote from "@/B2C/features/clients/ClientFetchQuote";
import CommissionLedgerPage from "@/B2C/features/CommissionLedger/CommissionLedgerPage";
import InvoicesPage from "@/B2C/features/Invoices/InvoicesPage";
import { hasModulePermission } from "@/D2C/features/auth/utils/permissions";
import { App_Url_New, Roles } from "@/shared/common/static";
import Modal from "@/shared/components/Modal";
import RightSidePanel from "@/shared/components/RightSidePanel";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { AppRoute } from "@/shared/types/routerType";
import { lazy } from "react";
import AuthLayout from "../B2C/layout/AuthLayout";
import ScrollToTop from "../shared/components/ScrollToTop";
import AppShell from "../shared/layout/AppShell";

// Admin Routes
export const Insurer = lazy(() => import("@/ADMIN/feature/insurar/InsurarPage"));
export const ApiConfiguration = lazy(() => import("@/ADMIN/feature/apiConfig/ApiConFigPage"));
export const ThirdPartyApi = lazy(() => import("@/ADMIN/feature/thirdPartyApi/ThirdPartyApiPage"));
export const Logs = lazy(() => import("@/ADMIN/feature/Logs-monitoring/LogPage"));
export const LogDetails = lazy(() => import("@/ADMIN/feature/Logs-monitoring/LogDetailsPage"));
export const Channels = lazy(() => import("@/ADMIN/feature/Channels/ChannelsPage"));

export const ServiceTemplate = lazy(
  () => import("@/ADMIN/feature/apiConfig/components/ServiceTemplatePage")
);
// D2C Routes
export const HomePage = lazy(() => import("@/D2C/features/home/HomePage"));
export const LoginPage = lazy(() => import("@/D2C/features/auth/AuthLogin"));
export const ViewPolicyPage = lazy(
  () => import("@/D2C/features/retail-dashboard/components/policies/ViewPolicyPage")
);
export const TravelInsurance = lazy(
  () => import("@/D2C/features/travel-insurance/TravelInsurancePage")
);
export const CarInsurance = lazy(() => import("@/D2C/features/car-insurance/CarInsurancePage"));
export const HealthInsurance = lazy(
  () => import("@/D2C/features/health-insurance/HealthInsurance")
);
export const LifeInsurance = lazy(() => import("@/D2C/features/life-insurance/LifeInsurancePage"));
export const TravelGetQuote = lazy(() => import("@/D2C/features/travel-quote/TravelQuotePage"));
export const CarGetQuote = lazy(() => import("@/D2C/features/car-quote/CarQuotePage"));
export const HealthGetQuote = lazy(() => import("@/D2C/features/health-quote/HealthQuotePage"));
export const LifeGetQuote = lazy(() => import("@/D2C/features/life-quote/LifeQuotePage"));
export const PolicyResult = lazy(() => import("@/D2C/features/policy/PolicyResultPage"));
export const RetailDashboard = lazy(
  () => import("@/D2C/features/retail-dashboard/RetailDashboardPage")
);

// B2C Routes
export const PoliciesPage = lazy(() => import("@/B2C/features/Policies/PoliciesPage"));
export const PipeLine = lazy(() => import("@/B2C/features/Pipeline/PipelinePage"));
export const Settings = lazy(() => import("@/B2C/features/setting/settingPage"));
export const LovableHub = lazy(() => import("@/B2C/features/lovable/LovableHubPage"));
export const commissionLedger = lazy(
  () => import("@/B2C/features/CommissionLedger/CommissionLedgerPage")
);
export const Calls = lazy(() => import("@/B2C/features/callLogs/callLogsPage"));
export const Dashboard = lazy(() => import("@/B2C/features/dashboard/dashboardIndex"));
export const Clients = lazy(() => import("@/B2C/features/clients/ClientPage"));
export const Quotes = lazy(() => import("@/B2C/features/quotes/QuotesPage"));
export const clientFetchQuote = lazy(() => import("@/B2C/features/clients/ClientFetchQuote"));

// public routes
// D2C routes
const publicRoutes: AppRoute[] = [
  // Home
  { path: App_Url_New.D2CPageUrl.home, element: <HomePage /> },

  {
    // Travel insurance flow
    path: App_Url_New.D2CPageUrl.insurance.travel,
    element: <TravelInsurance />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.travelGetQuote,
    element: <TravelGetQuote />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.travelPolicyResult,
    element: <PolicyResult lob="travel" />,
  },

  // Car insurance flow
  { path: App_Url_New.D2CPageUrl.insurance.car, element: <CarInsurance /> },

  {
    path: App_Url_New.D2CPageUrl.insurance.carGetQuote,
    element: <CarGetQuote />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.carPolicyResult,
    element: <PolicyResult lob="car" />,
  },

  // Health insurance flow
  {
    path: App_Url_New.D2CPageUrl.insurance.health,
    element: <HealthInsurance />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.healthGetQuote,
    element: <HealthGetQuote />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.life,
    element: <LifeInsurance />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.lifeGetQuote,
    element: <LifeGetQuote />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.lifePolicyResult,
    element: <PolicyResult lob="life" />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.healthPolicyResult,
    element: <PolicyResult lob="health" />,
  },

  // Retail dashboard and auth
  {
    path: App_Url_New.D2CPageUrl.retailDashboard,
    element: <RetailDashboard />,
  },

  { path: App_Url_New.CommonPageUrl.login, element: <LoginPage /> },
];

// privates rotes
// B2C routes
const privateRoutes: AppRoute[] = [
  {
    path: App_Url_New.B2CPageUrl.dashboard,
    element: <Dashboard />,
    module: "dashboard",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.clients,
    element: <Clients />,
    module: "clients",
    action: "list",
  },
  {
    path: App_Url_New.B2CPageUrl.quotes,
    element: <Quotes />,
    module: "quotes",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.addQuotes,
    element: <Navigate to={`${App_Url_New.B2CPageUrl.addQuotes}/travel`} replace />,
    module: "quotes",
    action: "view",
  },
  {
    path: `${App_Url_New.B2CPageUrl.addQuotes}/:product`,
    element: <Quotes />,
    module: "quotes",
    action: "view",
  },
  {
    path: "/add-quotes",
    element: <Navigate to={`${App_Url_New.B2CPageUrl.addQuotes}/travel`} replace />,
    module: "quotes",
    action: "view",
  },
  {
    path: "/add-quotes/:product",
    element: <Quotes />,
    module: "quotes",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.editQuote,
    element: <ClientFetchQuote />,
    module: "quotes",
    action: "view",
  },
  {
    path: "/auth/client/quotes",
    element: <ClientFetchQuote />,
    module: "quotes",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.commissionLedger,
    element: <CommissionLedgerPage />,
    module: "commission-ledger",
    action: "list",
  },
  {
    path: App_Url_New.B2CPageUrl.invoices,
    element: <InvoicesPage />,
    module: "invoices",
    action: "list",
  },
  {
    path: App_Url_New.B2CPageUrl.settings,
    element: <Settings />,
    module: "settings",
    action: "view",
  },
  {
    path: App_Url_New.AdminPageUrl.apiConfiguration,
    element: <ApiConfiguration />,
    module: "apiconfig",
    action: "list",
  },
  {
    path: App_Url_New.AdminPageUrl.channels,
    element: <Channels />,
    module: "channels",
    action: "list",
  },
  {
    path: App_Url_New.AdminPageUrl.reviewQueue,
    element: <ReviewQueuePage />,
    module: "review-queue",
    action: "list",
  },
  {
    path: App_Url_New.AdminPageUrl.thirdPartyApi,
    element: <ThirdPartyApi />,
    module: "api-monitoring",
    action: "list",
  },
  {
    path: App_Url_New.AdminPageUrl.insurer,
    element: <Insurer />,
    module: "insurerconfig",
    action: "view",
  },
  {
    path: App_Url_New.AdminPageUrl.apiConfiguration + "/:id" + "/:insurer_id",
    element: <ServiceTemplate />,
    module: "logs",
    action: "list",
  },
  {
    path: App_Url_New.AdminPageUrl.logs,
    element: <Logs />,
    module: "logs",
    action: "view",
  },
  {
    path: `${App_Url_New.AdminPageUrl.logs}/:log_id`,
    element: <LogDetails />,
    module: "logs",
    action: "view",
  },

  {
    path: App_Url_New.B2CPageUrl.calls,
    element: <LovableHub />,
    module: "calls",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.pipeline,
    element: <PipeLine />,
    module: "pipeline",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.policies,
    element: <PoliciesPage />,
    module: "policies",
    action: "view",
  },
  {
    path: `${App_Url_New.B2CPageUrl.policies}/:policyId`,
    element: <ViewPolicyPage />,
    module: "policies",
    action: "view",
  },

  // Lovable iframe routes -> https://insur-spark-dash.lovable.app/<path>
  ...[
    App_Url_New.B2CPageUrl.commissionDashboard,
    // App_Url_New.B2CPageUrl.commissionLedger,
    App_Url_New.B2CPageUrl.reconciliation,
    App_Url_New.B2CPageUrl.variances,
    App_Url_New.B2CPageUrl.tds,
    App_Url_New.B2CPageUrl.reports,
    App_Url_New.B2CPageUrl.irdaiReports,
    App_Url_New.B2CPageUrl.configuration,
    App_Url_New.B2CPageUrl.cases,
    App_Url_New.B2CPageUrl.claims,
  ].flatMap((basePath) => [
    {
      path: basePath,
      element: <LovableHub />,
      module:
        basePath === App_Url_New.B2CPageUrl.commissionDashboard
          ? "commission-dashboard"
          : basePath === App_Url_New.B2CPageUrl.irdaiReports
            ? "irdai-compliance"
            : basePath === App_Url_New.B2CPageUrl.cases
              ? "case"
              : basePath === App_Url_New.B2CPageUrl.claims
                ? "claims"
                : "dashboard",
    },
    {
      path: `${basePath}/*`,
      element: <LovableHub />,
      module:
        basePath === App_Url_New.B2CPageUrl.commissionDashboard
          ? "commission-dashboard"
          : basePath === App_Url_New.B2CPageUrl.irdaiReports
            ? "irdai-compliance"
            : basePath === App_Url_New.B2CPageUrl.cases
              ? "case"
              : basePath === App_Url_New.B2CPageUrl.claims
                ? "claims"
                : "dashboard",
    },
  ]),
];

export default function AppRouter() {
  const {
    auth: { user, customerAuth },
  } = usePosterReducers();

  const hasUserAccessToken = Boolean(user?.access_token || user?.user?.access_token);
  const hasCustomerAccessToken = Boolean(customerAuth?.access_token);
  const isAdmin = user?.user?.roles?.some(
    (role: { name?: string }) => role.name?.trim().toLowerCase() === Roles.ADMIN
  );

  const isAccounts = user?.user?.roles?.some(
    (role: { name?: string }) => role.name?.trim().toLowerCase() === Roles.ACCOUNTS
  );

  const allowedPrivateRoutes = privateRoutes.filter(
    (route) => isAdmin || hasModulePermission(user?.user, route.module)
  );

  const fallbackPrivatePath = isAdmin
    ? App_Url_New.AdminPageUrl.thirdPartyApi
    : isAccounts
      ? App_Url_New.B2CPageUrl.commissionDashboard
      : allowedPrivateRoutes[0]?.path || App_Url_New.B2CPageUrl.dashboard;

  const renderRoutes = (routes: AppRoute[]) =>
    routes.map(({ path, element }) => {
      const isLoginRoute = path === App_Url_New.CommonPageUrl.login;
      const loginRedirect = hasCustomerAccessToken
        ? App_Url_New.D2CPageUrl.retailDashboard
        : fallbackPrivatePath;

      return (
        <Route
          key={path}
          path={path}
          element={
            isLoginRoute && (hasUserAccessToken || hasCustomerAccessToken) ? (
              <Navigate to={loginRedirect} replace />
            ) : (
              element
            )
          }
        />
      );
    });

  return (
    <>
      <ScrollToTop />
      <Routes>
        {user ? (
          <>
            <Route element={<AuthLayout />}>{renderRoutes(allowedPrivateRoutes)}</Route>
            <Route path="*" element={<Navigate to={fallbackPrivatePath} replace />} />
          </>
        ) : (
          <>
            <Route element={<AppShell />}>{renderRoutes(publicRoutes)}</Route>
            <Route path="*" element={<Navigate to={App_Url_New.D2CPageUrl.home} replace />} />
          </>
        )}
      </Routes>
      <Modal />
      <RightSidePanel />
    </>
  );
}
